/**
 * PATH: packages/bills/src/components/format-datetime.ts
 * FILE_ID: BILLS-PKG-UTIL-DATE-003
 * PURPOSE: Absolute + relative date formatting for the bill panel's
 *          "received" metadata row.
 *
 * WHY: Bookkeepers triaging dozens of extracted bills need to gauge
 *      staleness at a glance ("3 days ago") without doing datetime
 *      arithmetic. Both formatters are pure and tolerate bad input by
 *      degrading (raw string / null) instead of rendering "NaN".
 *
 * Extracted verbatim from BillDataPanel.tsx (P4-1 split) — no behaviour change.
 */

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Compact, date-only label ("31 Jul 2026") for the summary card's
 * "Due <date>" subline. Date-only strings (YYYY-MM-DD, as the API
 * ships for invoice_date / due_date) are parsed as *local* time so a
 * negative UTC offset can never bump the day backwards. Returns null on
 * bad/empty input so the caller can drop the segment entirely.
 */
export function formatShortDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return d.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}

/**
 * "3 days ago" / "in 2 hours" — calibrated for AP-team usage where
 * the absolute date is already shown next to it. Returns null when
 * the input is unparseable so the chip is suppressed (better than
 * showing "NaN ago").
 */
export function formatRelative(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diffMs = d.getTime() - Date.now();
  const sign = diffMs >= 0 ? 1 : -1;
  const abs = Math.abs(diffMs);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;
  let value: number;
  let unit: Intl.RelativeTimeFormatUnit;
  if (abs < hour) {
    value = Math.round(abs / minute);
    unit = 'minute';
  } else if (abs < day) {
    value = Math.round(abs / hour);
    unit = 'hour';
  } else if (abs < week) {
    value = Math.round(abs / day);
    unit = 'day';
  } else {
    value = Math.round(abs / week);
    unit = 'week';
  }
  try {
    return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(
      sign * value,
      unit,
    );
  } catch {
    return null;
  }
}
